// MembersPage — heading card, seat metrics, members table, pending-invite table.
import { useCallback, useEffect, useState } from 'react';
import { useOrgPageMeta, useOrgSlug } from '@/layouts/orgSettingsMeta';
import { organizationService } from '@/services/api';
import type { InvitesData, MemberAction, MemberStatus } from '@/services/api/organizationService';
import { BaseTable, Button, Modal, Select } from '@/components/ui';
import type { BaseTableColumn } from '@/components/ui';
import { useToast } from '@/hooks/useToast';
import { useConfirm } from '@/hooks/useConfirm';
import { useModal } from '@/hooks/useModal';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTimeShort } from '@/lib/datetime';
import { normalizeApiError } from '@/lib/normalizeApiError';
import OrgSectionHeading from '@/features/organization/OrgSectionHeading';
import type { Member, PendingInvite, Seats } from '@/types';

const STATUS_ORDER: Record<Member['orgStatus'], number> = {
  pending: 0,
  active: 1,
  inactive: 2,
};
const FETCH_STATUSES: MemberStatus[] = ['pending', 'active', 'inactive'];
const PER_PAGE = 100;
const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];
const INVITE_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

function RoleBadge({ role }: { role: string }) {
  const normalized = role || 'member';
  const variant = normalized === 'owner' || normalized === 'admin' ? normalized : 'member';
  const label = normalized === 'owner' ? 'Owner' : normalized === 'admin' ? 'Admin' : 'Member';
  return <span className={`org-role-badge org-role-badge--${variant}`}>{label}</span>;
}

async function fetchAllForStatus(slug: string, status: MemberStatus): Promise<Member[]> {
  const first = await organizationService.getMembers(slug, {
    status,
    page: 1,
    perPage: PER_PAGE,
  });
  if (first.totalPages <= 1) return first.records;
  const rest = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      organizationService.getMembers(slug, { status, page: i + 2, perPage: PER_PAGE }),
    ),
  );
  return first.records.concat(rest.flatMap((page) => page.records));
}

export default function MembersPage() {
  const slug = useOrgSlug();
  useOrgPageMeta('Members', "Manage your organization's members and access.");

  const { currentUser, isAdmin } = useAuth();
  const isOwner = currentUser?.orgRole === 'owner' || isAdmin;

  const toast = useToast();
  const confirm = useConfirm();
  const inviteModal = useModal();

  const [members, setMembers] = useState<Member[]>([]);
  const [inviteData, setInviteData] = useState<InvitesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [sending, setSending] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState('');

  const loadMembers = useCallback(() => {
    return Promise.all(FETCH_STATUSES.map((status) => fetchAllForStatus(slug, status))).then(
      (batches) => {
        const merged = batches.flat().sort((a, b) => {
          const byStatus = STATUS_ORDER[a.orgStatus] - STATUS_ORDER[b.orgStatus];
          if (byStatus !== 0) return byStatus;
          return (a.email || '').localeCompare(b.email || '');
        });
        setMembers(merged);
      },
    );
  }, [slug]);

  const loadInvites = useCallback(() => {
    return organizationService.getInvites(slug).then(setInviteData);
  }, [slug]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([loadMembers(), loadInvites()]).finally(() => setLoading(false));
  }, [loadMembers, loadInvites]);

  useEffect(() => {
    load();
  }, [load]);

  const updateMemberRole = async (member: Member, newRole: string) => {
    setBusyId(member.id);
    try {
      const result = await organizationService.memberAction(slug, {
        user_id: member.id,
        action: 'change_role',
        role: newRole,
      });
      toast.success(result.message || `Role updated to ${newRole}.`);
      load();
    } catch (err) {
      toast.error(normalizeApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const runAction = async (member: Member, action: MemberAction, confirmMessage?: string) => {
    if (confirmMessage) {
      const ok = await confirm({ title: confirmMessage });
      if (!ok) return;
    }
    setBusyId(member.id);
    try {
      const result = await organizationService.memberAction(slug, {
        user_id: member.id,
        action,
      });
      toast.success(result.message || 'Done.');
      load();
    } catch (err) {
      toast.error(normalizeApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inviteEmail.trim();
    if (!trimmed) return;
    setSending(true);
    setAcceptUrl('');
    try {
      const result = await organizationService.sendInvite(slug, {
        email: trimmed,
        role: inviteRole,
      });
      toast.success(`Invite sent to ${result.email} (${result.role}).`);
      setAcceptUrl(result.acceptUrl);
      setInviteEmail('');
      setInviteRole('member');
      loadInvites();
    } catch (err) {
      toast.error(normalizeApiError(err));
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (invite: PendingInvite) => {
    const ok = await confirm({ title: 'Revoke this invitation?', confirmText: 'Revoke' });
    if (!ok) return;
    try {
      const result = await organizationService.revokeInvite(slug, invite.id);
      toast.success(result.message || 'Invite revoked.');
      loadInvites();
    } catch (err) {
      toast.error(normalizeApiError(err));
    }
  };

  const columns: BaseTableColumn<Member>[] = [
    { key: 'email', header: 'Email' },
    { key: 'name', header: 'Name', render: (m) => m.name || '—' },
    {
      key: 'role',
      header: 'Role',
      render: (m) => {
        const role = m.orgRole || 'member';
        const editable = isOwner && m.orgStatus === 'active' && role !== 'owner';
        const options =
          role === 'owner' ? [{ value: 'owner', label: 'Owner' }, ...ROLE_OPTIONS] : ROLE_OPTIONS;
        return (
          <Select
            size="sm"
            portal
            className="org-members-role-select"
            menuClassName="org-members-role-menu"
            value={role}
            options={options}
            disabled={!editable || busyId === m.id}
            onChange={(value) => updateMemberRole(m, value)}
            aria-label={`Change role for ${m.name || m.email}`}
          />
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) => m.orgStatus || '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (m) => {
        if (m.orgStatus === 'pending') {
          return (
            <div className="cell-actions">
              <Button
                size="sm"
                pill
                disabled={busyId === m.id}
                onClick={() => runAction(m, 'approve')}
              >
                Approve
              </Button>
              <Button
                size="sm"
                pill
                variant="danger"
                disabled={busyId === m.id}
                onClick={() => runAction(m, 'reject', 'Reject this membership request?')}
              >
                Reject
              </Button>
            </div>
          );
        }
        // The owner can't be suspended or removed (it would orphan the org). The backend also
        // rejects these actions for the owner; hiding the buttons keeps the UI honest.
        if (m.orgStatus === 'active' && m.orgRole !== 'owner') {
          return (
            <div className="cell-actions">
              <Button
                size="sm"
                pill
                variant="secondary"
                disabled={busyId === m.id}
                onClick={() => runAction(m, 'suspend', 'Suspend this user?')}
              >
                Suspend
              </Button>
              <Button
                size="sm"
                pill
                variant="danger"
                disabled={busyId === m.id}
                onClick={() => runAction(m, 'remove', 'Remove this user from the organization?')}
              >
                Remove
              </Button>
            </div>
          );
        }
        return '—';
      },
    },
  ];

  const inviteColumns: BaseTableColumn<PendingInvite>[] = [
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (inv) => <RoleBadge role={inv.orgRole || 'member'} />,
    },
    {
      key: 'sent',
      header: 'Sent',
      render: (inv) => (inv.createdAt ? formatDateTimeShort(inv.createdAt) : '—'),
    },
    {
      key: 'expires',
      header: 'Expires',
      render: (inv) => (inv.expiresAt ? formatDateTimeShort(inv.expiresAt) : '—'),
    },
    {
      key: 'revoke',
      header: 'Actions',
      render: (inv) =>
        inviteData?.isAdmin ? (
          <Button size="sm" pill variant="danger" onClick={() => handleRevoke(inv)}>
            Revoke
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  const seats: Seats | undefined = inviteData?.seats;
  const pendingInvites = inviteData?.pendingInvites ?? [];
  const canInvite = Boolean(inviteData?.isAdmin);

  return (
    <>
      <div className="org-metrics is-two">
        <div className="org-metric-card">
          <h2 className="org-section-title">Seats</h2>
          <p className="org-stat-value">
            {!seats
              ? '—'
              : seats.included === null
                ? seats.active
                : `${seats.used} / ${seats.included}`}
          </p>
          <p className="org-section-sub">
            {!seats
              ? 'Loading seat usage'
              : seats.included === null
                ? `${seats.pending} pending invites · limit not enforced`
                : `${seats.active} active + ${seats.pending} pending`}
          </p>
        </div>
        <div className="org-metric-card">
          <h2 className="org-section-title">Pending invites</h2>
          <p className="org-stat-value">{seats ? seats.pending : '—'}</p>
          <p className="org-section-sub">
            {seats?.remaining !== null && seats?.remaining !== undefined && seats.remaining <= 0
              ? 'All seats taken'
              : 'Awaiting acceptance'}
          </p>
        </div>
      </div>

      <div className="org-panel">
        <OrgSectionHeading
          plain
          title="Members"
          actions={
            <Button pill onClick={inviteModal.show}>
              <i className="bi bi-plus-lg me-1" aria-hidden />
              Invite Member
            </Button>
          }
        />
        <BaseTable
          className="org-members-table"
          columns={columns}
          rows={members}
          loading={loading}
          getRowKey={(m) => m.id}
          emptyMessage="No members found."
        />

        {pendingInvites.length > 0 && (
          <>
            <OrgSectionHeading plain title="Pending invites" />
            <BaseTable
              className="org-members-table"
              columns={inviteColumns}
              rows={pendingInvites}
              getRowKey={(inv) => inv.id}
              emptyMessage="No pending invites."
            />
          </>
        )}
      </div>

      <Modal
        open={inviteModal.open}
        onClose={() => {
          inviteModal.close();
          setAcceptUrl('');
        }}
        title="Invite member"
        size="md"
      >
        <p className="org-section-sub mb-3">
          Work email only; must match a verified domain. Only owners and billing admins can send or
          revoke.
        </p>
        {seats?.remaining !== null && seats?.remaining !== undefined && seats.remaining <= 0 && (
          <div className="alert alert-warning py-2 small mb-3">
            <i className="bi bi-exclamation-triangle me-2" />
            All seats are currently allocated ({seats.used} of {seats.included} used). Inviting
            additional members requires upgrading your organization plan.
          </div>
        )}
        {canInvite ? (
          <form className="org-invite-form org-invite-form--modal" onSubmit={handleSendInvite}>
            <div>
              <label className="org-label" htmlFor="org-invite-email">
                Email
              </label>
              <input
                id="org-invite-email"
                type="email"
                className="form-control"
                placeholder="teammate@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="org-label" htmlFor="org-invite-role">
                Role
              </label>
              <Select
                id="org-invite-role"
                value={inviteRole}
                onChange={(v) => setInviteRole(v as 'admin' | 'member')}
                options={INVITE_ROLE_OPTIONS}
                portal
              />
            </div>
            <Button type="submit" pill loading={sending}>
              Send invite
            </Button>
          </form>
        ) : (
          <p className="text-muted small mb-0">
            Ask your organization owner or billing admin to send email invitations.
          </p>
        )}
        {acceptUrl && (
          <div className="alert alert-success mt-3 mb-0">
            Invite sent. Share link if needed: {acceptUrl}
          </div>
        )}
      </Modal>
    </>
  );
}
